/* eslint-disable class-methods-use-this */
/* eslint-disable no-unused-expressions */
import {
  CSSResult, html, LitElement, nothing, unsafeCSS,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import arrowRight
  from '@breuni-ui/nui/dist/design-tokens/images/icons/nui-arrow_chevron_right_direction=right_size=18_weight=regular.svg';
import baseInfo from '@breuni-ui/nui/dist/design-tokens/images/icons/base_info_size=18_weight=light.svg';
import styles from './hva.scss';
import { EntsModal } from '../modal/modal.js';
import { OpenHvaModalEvent } from '../../@types/dom.js';
import { HvaModalModel } from './hva-modal-model.js';
import {
  Config, HvaModel, OfflineStore, OfflineStoreStock,
} from '../../@types/hva.js';
import { LoadingState } from './loading-state.js';

@customElement('ents-hva')
export class Hva extends LitElement {
  @property({ type: Object, reflect: false }) config: Config = { messages: {}, assetHost: '' };

  @state() private hvaModalModel = HvaModalModel.empty();

  @state() private loadingState = LoadingState.loading();

  // use this syntax to make it work in Safari 14
  // working: static get styles() { return css`...` }
  // not working: static styles = css`...`
  static get styles(): CSSResult {
    return unsafeCSS(styles);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('kaufen:reserve:back', () => this.showModal());
    document.addEventListener('ents:hva-modal:open', event => this.handleOpenEvent(event));
    document.addEventListener('kaufen:reserve:success', () => this.hvaModalModel = HvaModalModel.empty());
  }

  private handleOpenEvent(event: OpenHvaModalEvent) {
    this.loadHvaModel(event.detail.articleId);
    this.showModal();
  }

  private renderStoreSelect() {
    return html`
      <div class="ents-hva__haus-select" data-test-ents-hva-haus-select>
        <img class="ents-hva__haus-select__img"
             src="${this.config.assetHost}bewerten/pds/images/hva/Teaser_Haueserverfuegbarkeit_XXL.jpg" alt=""
             loading="lazy">
        <div class="ents-hva__haus-select__select ents-hva__haus-select__select--mobile">
          <select class="ents-prose-copy" name="ents-hva-select"
                  @change="${(event: Event & { target: HTMLSelectElement }) => this.selectStore(+event.target.value)}"
                  data-test-ents-hva-haus-select-mobile>
            ${this.hvaModalModel.getOptions().map(optionModel => html`
              <option value="${optionModel.value}" ?selected="${this.hvaModalModel.isOptionSelected(optionModel)}">
                ${optionModel.name}
              </option>
            `)}
          </select>
          ${arrowRight()}
        </div>
        <ents-hva-custom-select .hvaModalModel="${this.hvaModalModel}"
          @change="${(e: CustomEvent) => this.selectStore(e.detail.selected)}"/>
      </div>
    `;
  }

  private renderStoreInfo(selectedStore: OfflineStore) {
    return html`
      <div class="ents-hva__haus-info">
        <hr class="ents-hva__line">
        <div class="ents-copy-small ents-hva__haus-info__details" data-test-ents-hva-address>
          <p>
            <span class="ents-copy-small-bold ents-hva__haus-info__details__headline">
              ${this.config.messages.addressHeadline}
            </span>
            ${selectedStore.adresse.praefix ? html`<span>${selectedStore.adresse.praefix}</span><br/>` : nothing}
            <span>${selectedStore.adresse.strasse}</span><br/>
            <span>${`${selectedStore.adresse.postleitzahl} ${selectedStore.adresse.stadt}`}</span><br/>
            <span>${`${this.config.messages.telPrefix} ${selectedStore.adresse.telefon}`}</span><br/>
          </p>
        </div>
        <div class="ents-copy-small ents-hva__haus-info__details" data-test-ents-hva-opening-times>
          <p>
            <span class="ents-copy-small-bold ents-hva__haus-info__details__headline">
              ${this.config.messages.openingHours}
            </span>
            ${selectedStore.oeffnungszeiten.map(line => html`<span>${line}</span><br/>`)}
          </p>
        </div>
      </div>
    `;
  }

  private renderHvaContent(selectedHva: HvaModel) {
    const selectedStore = selectedHva.haus;
    return html`
      ${selectedHva.hausbestaende.length > 0 ? this.renderAvailableStock(selectedHva) : this.renderOutOfStockHints(selectedStore)}
      ${this.renderStoreInfo(selectedStore)}
    `;
  }

  private renderAvailableStock(selectedHva: HvaModel) {
    const selectedStore = selectedHva.haus;
    const stocksInStore = selectedHva.hausbestaende;
    return html`
      ${this.renderProductName()}
      ${this.renderSizeHint()}
      <div
          class="ents-hva__sizes ${classMap({ 'ents-hva__sizes--single-size': stocksInStore.length === 1 })}">
        ${stocksInStore.map(storeStock => this.renderSize(storeStock, this.hvaModalModel.reservationEnabled()))}
      </div>
      ${this.renderOnlineReservation(selectedStore)}
      <div class="ents-copy ents-hva__hint ents-hva__hint--disclaimer">
        ${baseInfo()}
        <p class="ents-copy-extra-small">${this.config.messages.disclaimer}</p>
      </div>
    `;
  }

  private renderProductName() {
    return html`
      <div class="ents-hva__product">
        <div class="ents-hva__product__name">
          <p class="ents-copy-bold ents-hva__product__name__name" data-test-ents-hva-product-name>
            ${this.hvaModalModel.productName}
          </p>
          <p class="ents-copy ents-hva__product__name__subname" data-test-ents-hva-product-subname>
            ${this.hvaModalModel.subName}
          </p>
        </div>
      </div>
    `;
  }

  private renderSize(storeStock: OfflineStoreStock, selectable: boolean) {
    return html`
      <div class="ents-hva__size ${classMap({
    'ents-hva__size--selected': this.hvaModalModel.sizeSelected(storeStock.id),
    'ents-hva__size--selectable': selectable,
  })}"
           key="${storeStock.id}"
           @click="${() => this.toggleSizeSelection(storeStock)}"
           data-test-ents-hva-hausbestand>
        <span class="ents-copy-bold ents-hva__size__size">${storeStock.label}</span>
        ${storeStock.bestand < 3 ? html`
          <span class="ents-copy-small ents-hva__size__stock ents-hva__size__stock--low">
              ${this.config.messages.lowStock}
            </span>
        ` : html`
          <span class="ents-copy-small ents-hva__size__stock">
              ${this.config.messages.inStock}
            </span>
        `}
      </div>
    `;
  }

  private renderOnlineReservation(selectedStore: OfflineStore) {
    return selectedStore.onlinereservierungAktiv
      ? html`
        <p class="ents-copy-small ents-hva__hint">
          ${unsafeHTML(this.config.messages.info)}
        </p>
        <div class="ents-hva__button">
          <div class="bewerten-button-container">
            <button ?disabled="${this.hvaModalModel.selectedSizesCount() === 0}" @click="${this.reserve}"
              class="nui-button-primary reserve-button" data-test-ents-hva-reserve-button>
              <span class="ents-heading ents-text-uppercase">
                ${this.config.messages.reserve} (${this.hvaModalModel.selectedSizesCount()})
              </span>
            </button>
          </div>
        </div>
      ` : html`
        <p class="ents-copy-bold ents-hva__hint ents-hva__hint--bold" data-test-ents-hva-reserve-disabled>
          ${this.config.messages.unavailable}
        </p>
      `;
  }

  private renderSizeHint() {
    return html`
      ${this.hvaModalModel.maximumSizesSelectedError ? html`
        <p class="ents-copy ents-hva__hint ents-hva__hint--error">
          ${this.config.messages.error1}
        </p>
      ` : nothing}
      ${!this.hvaModalModel.maximumSizesSelectedError && this.hvaModalModel.reservationEnabled() ? html`
        <p class="ents-copy-small ents-hva__hint">
          ${this.config.messages.sizeHint}
        </p>
      ` : nothing}
    `;
  }

  private renderOutOfStockHints(selectedStore: OfflineStore) {
    return html`
      <div class="ents-hva__article-not-available" data-test-ents-hva-article-not-available-error>
        <p class="ents-copy-bold ents-hva__hint ents-hva__hint--error ents-hva__hint--bold">
          ${this.config.messages.productOutOfStock}</p>
        <p class="ents-copy ents-hva__hint">
          ${this.config.messages.hint.replace('HINT_SELECTED_STORE', selectedStore.name)}
        </p>
        <div class="bewerten-button-container ents-hva__button">
          <button @click="${this.closeModal}" type="button" class="nui-button-primary">
            <span class="ents-heading ents-text-uppercase">${this.config.messages.backToArticle}</span>
          </button>
        </div>
      </div>`;
  }

  private renderLoaded() {
    const selectedHva = this.hvaModalModel.selectedHva();
    return selectedHva ? this.renderHvaContent(selectedHva) : this.renderUnselectedStoreHintOrError();
  }

  private renderUnselectedStoreHintOrError() {
    return this.loadingState.isFailed
      ? html`
          <p class="ents-copy ents-hva__hint ents-hva__hint--error">
            ${this.config.messages.error3}
          </p>
        `
      : html`
        <div class="ents-hva__choose-haus" data-test-ents-hva-initial-information>
          <img src="${this.config.assetHost}bewerten/pds/images/hva/ic-haeuserverfuegbarkeit-grey.svg" alt="" loading="lazy">
          <p class="ents-copy ents-hva__choose-haus__text">${this.config.messages.chooseStore}</p>
        </div>
      `;
  }

  private renderSpinner() {
    return html`
      <div class="ents-hva__loading">
        <div class="bui-spinner"><svg viewBox="0 0 50 50"><circle cx="25" cy="25" r="20"></circle></svg></div>
      </div>
    `;
  }

  protected render() {
    return html`
      <ents-modal id="hvaModal" class="ents-hva__modal" ref="hvaModal">
        <div slot="title" class="ents-hva__modal__header">
          <h3 class="ents-heading m-0 pt-0">${this.config.messages.title}</h3>
        </div>
        <div class="ents-hva__modal__content">
          ${this.renderStoreSelect()}
          ${this.loadingState.isLoading ? this.renderSpinner() : this.renderLoaded()}
        </div>
      </ents-modal>
    `;
  }

  private reserve() {
    const payload = this.hvaModalModel.reserve();
    this.setAttribute('data-test-ents-hva-payload-emitted', JSON.stringify(payload));
    this.closeModal();
  }

  private toggleSizeSelection(hausbestand: OfflineStoreStock) {
    this.hvaModalModel = this.hvaModalModel.toggleSizeSelection(hausbestand.id);
  }

  private selectStore(storeId: number) {
    this.hvaModalModel = this.hvaModalModel.applyOption(storeId);
  }

  private doOnModal(action: (modal: EntsModal) => void) {
    const modal = this.shadowRoot?.querySelector<EntsModal>('#hvaModal');
    if (modal) {
      action(modal);
      return true;
    }
    return false;
  }

  private closeModal() {
    return this.doOnModal(modal => modal.hide());
  }

  private showModal() {
    return this.doOnModal(modal => modal.show());
  }

  private loadHvaModel(variantId: string) {
    if (!this.hvaModalModel.shouldReload(variantId)) {
      return;
    }
    this.loadingState = LoadingState.loading();
    const hvaUri = this.config.getHvaModelUrl.replace('VARIANT_ID', variantId);
    fetch(hvaUri)
      .then(response => response.json())
      .then((response) => {
        this.hvaModalModel = HvaModalModel.create(variantId, response, this.config);
        this.loadingState = LoadingState.loaded();
      })
      .catch(() => {
        this.loadingState = LoadingState.loadedWithError();
      });
  }
}
